/**
 * Core: Get Product
 * ------------------------------------------------------------------------------
 * - Queries the Storefront API and returns an already-formatted product object.
 */
import productQuery from '~/graphql/product.gql'
import linkedProductsQuery from '~/graphql/linked-style-products.gql'

import { getProductTagValueByKey } from '~/helpers/product'

import {
  formatLinkedStyles,
  formatProduct,
} from '~/format/product'

/**
 * Return a formatted Shopify product object.
 * - Query Storefront API for a given handle
 * - Format the response object.
 * - Check for any LinkedStyles
 * - Query Storefront API for linked styles
 * - Format response and append to product object
 * - Format response and append to product object
 *
 * * Note - the nested promises here are messy. Aim to refactor.
 * - The bad nesting *does* allow for bespoke error logging, but still fix.
 * @param {Object} data - Parameters.
 * @param {Object} data.handle - Shopify product handle.
 * @param {String} data.id - Shopify product id.
 * @param {Boolean|String} [data.flexi] - String value for FlexiFashion query parameter.
 * @returns {Object}
 */
export async function getProduct({ handle, id, flexi = false }) {
  try {
    let linkedStyleProducts = false

    const storefrontResponse = await mint.Query({
      query: productQuery,
      variables: {
        country: mint.shop.country,
        handle: handle,
        id: id,
        language: mint.shop.language,
      },
    })

    const responseProduct = storefrontResponse.product

    if (!responseProduct || !Object.keys(responseProduct).length) {
      throw new Error('No product returned from Storefront API', {
        handle,
        id,
      })
    }

    let formattedProduct = formatProduct({
      responseProduct,
      currentFlexiFashionHandle: flexi,
    })

    const linkedStyleReference = getProductTagValueByKey(
      formattedProduct.tags,
      'LinkedStyleReference',
    )

    /**
     * Only perform query if a tag exists.
     */
    if (linkedStyleReference) {
      linkedStyleProducts = await getLinkedStyleProducts({
        tag: `LinkedStyleReference:${linkedStyleReference}`,
        parentHandle: formattedProduct.handle,
      })
    }

    if (linkedStyleProducts) {
      formattedProduct = {
        ...formattedProduct,
        linkedStyles: linkedStyleProducts,
      }
    }

    return formattedProduct

  } catch (error) {
    return error
  }
}

/**
 * Return a formatted 'Linked Styles' object.
 * @param {String} [data] - Parameters.
 * @param {String} [data.tag] - 'LinkedStyleReference:' tag from a given product.
 * @param {String} [data.handle] - Handle of the product this array is being added to.
 * @returns {Promise}
 */
export function getLinkedStyleProducts({ tag, parentHandle }) {
  return new Promise((resolve, reject) => {
    mint.Query({
      query: linkedProductsQuery,
      variables: {
        query: `tag:${tag}`,
        country: mint.shop.country,
        language: mint.shop.language,
      },
    })
      .then((response) => {
        const colours = formatLinkedStyles(response, parentHandle, tag)

        resolve(colours)
      })
      .catch((error) => {
        reject(error)
      })
  })
}
